import { useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import { api, abs, errMsg, TYPE_LABELS } from "../../lib/api";
import { Loading } from "../../components/PageHeader";
import { Switch } from "../../components/ui/switch";
import { AdminTitle, ErrorBox } from "./AdminLayout";
import { SingleImage, ImagesField } from "./Uploaders";

function WatermarkToggle() {
  const [on, setOn] = useState(null);
  useEffect(() => { api.get("/admin/settings").then((r) => setOn(r.data.watermark_previews)).catch((e) => toast.error(errMsg(e))); }, []);
  const toggle = async (v) => {
    try { await api.put("/admin/settings", { watermark_previews: v }); setOn(v); toast.success(v ? "Watermarks enabled on previews" : "Watermarks disabled"); }
    catch (e) { toast.error(errMsg(e)); }
  };
  return (
    <label className="wb-card flex items-center gap-4 p-4 text-sm" data-testid="watermark-setting">
      <Switch checked={!!on} disabled={on === null} onCheckedChange={toggle} data-testid="watermark-switch" />
      Watermark public previews
    </label>
  );
}

function FeaturedPicker({ products, ids, setIds }) {
  const toggle = (id) => setIds(ids.includes(id) ? ids.filter((x) => x !== id) : [...ids, id]);
  const active = useMemo(() => products.filter((p) => p.active), [products]);
  return (
    <div className="grid gap-3 sm:grid-cols-2 xl:grid-cols-3" data-testid="featured-picker">
      {active.map((p) => {
        const on = ids.includes(p.id);
        return (
          <button type="button" key={p.id} onClick={() => toggle(p.id)} data-testid={`featured-toggle-${p.slug}`}
            className={`flex items-center gap-3 border p-3 text-left transition-colors ${on ? "border-[color:var(--wb-gold)] bg-wb-card" : "wb-line hover:border-[color:var(--wb-line-accent)]"}`}>
            {p.image_urls[0] && <img src={abs(p.image_urls[0])} alt="" className="h-12 w-12 object-cover" />}
            <span className="flex-1 text-sm">{p.title}<span className="block text-xs muted">{TYPE_LABELS[p.type]}</span></span>
            <span className={`h-4 w-4 border ${on ? "bg-wb-gold border-transparent" : "wb-line"}`} />
          </button>
        );
      })}
    </div>
  );
}

const Section = ({ title, children, hint }) => (
  <section className="wb-card p-6 md:p-8">
    <div className="eyebrow">{title}</div>
    {hint && <p className="mt-2 text-xs muted">{hint}</p>}
    <div className="mt-6">{children}</div>
  </section>
);

export default function SiteContent() {
  const [s, setS] = useState(null);
  const [products, setProducts] = useState([]);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");
  useEffect(() => {
    api.get("/admin/site").then((r) => setS(r.data)).catch((e) => setErr(errMsg(e)));
    api.get("/admin/products").then((r) => setProducts(r.data)).catch((e) => toast.error(errMsg(e)));
  }, []);
  const set = (k, v) => setS((p) => ({ ...p, [k]: v }));
  const save = async () => {
    setBusy(true);
    try {
      const { data } = await api.put("/admin/site", {
        hero_image: s.hero_image, about_portrait: s.about_portrait, about_portrait_caption: s.about_portrait_caption,
        about_images: s.about_images, featured_ids: s.featured_ids,
      });
      setS(data); toast.success("Site content saved — changes are live");
    } catch (e) { toast.error(errMsg(e)); }
    setBusy(false);
  };
  if (err) return <ErrorBox msg={err} />;
  if (!s) return <Loading />;
  const heroDefault = abs(products.find((p) => p.slug === "kinabalu-reverie")?.image_urls[0] || "");
  return (
    <div className="space-y-8" data-testid="admin-site-content">
      <AdminTitle eyebrow="Site content" title="Homepage & About">
        <WatermarkToggle />
        <button onClick={save} disabled={busy} className="btn-gold" data-testid="site-save-button">{busy ? "Saving…" : "Save changes"}</button>
      </AdminTitle>
      <Section title="Homepage hero" hint="Large landscape artwork works best (at least 1600px wide). Watermarked when previews watermarking is on.">
        <SingleImage label="Hero image" kind="hero" value={s.hero_image} onChange={(v) => set("hero_image", v)} fallback={heroDefault} testid="site-hero" />
      </Section>
      <Section title="Homepage featured works" hint="Selected works appear in “From the studio” on the homepage.">
        <FeaturedPicker products={products} ids={s.featured_ids} setIds={(v) => set("featured_ids", v)} />
      </Section>
      <Section title="About page">
        <div className="grid gap-8 lg:grid-cols-2">
          <div className="space-y-4">
            <SingleImage label="Artist portrait" kind="site" value={s.about_portrait} onChange={(v) => set("about_portrait", v)} fallback="/images/about-studio.jpg" testid="site-portrait" hint="Portrait orientation (4:5) recommended; framed from the top so faces stay visible." />
            <div>
              <label className="wb-label">Portrait caption</label>
              <input className="wb-input" value={s.about_portrait_caption} onChange={(e) => set("about_portrait_caption", e.target.value)} data-testid="site-portrait-caption-input" />
            </div>
          </div>
          <div>
            <div className="wb-label">Secondary images</div>
            <p className="mb-4 text-xs muted">First image appears beside the bio; others form a strip below. Leave empty to use the default.</p>
            <ImagesField images={s.about_images} setImages={(fn) => set("about_images", fn(s.about_images))} onMeta={() => {}} kind="site" testid="site-about-images" />
          </div>
        </div>
      </Section>
    </div>
  );
}
